import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { cx } from "@zenchef/styled-system/css";
import React, {
  useEffect,
  ReactElement,
  useCallback,
  createContext,
} from "react";
import { Box, BoxProps } from "@zenchef/styled-system/jsx";
import { aromaDraggableContainer } from "@zenchef/styled-system/recipes";
import DraggableItem, {
  DraggableItemProps,
  DraggableItemData,
} from "./DraggableItem";
import useFlipDraggableAnimation from "./useFlipDraggableAnimation";

type DraggableItemComponent = ReactElement<
  DraggableItemProps,
  typeof DraggableItem
>;

export interface SortEndData {
  source: {
    id: DraggableItemData["id"];
    index: number;
  };
  destination: {
    id: DraggableItemData["id"];
    index: number;
  };
}
export const DraggableContext = createContext<{
  moveItem: (id: DraggableItemData["id"], direction: "up" | "down") => void;
  flow: "vertical" | "horizontal";
} | null>(null);

interface DraggableContainerProps extends BoxProps {
  children: React.ReactNode;
  flow?: "vertical" | "horizontal";
  onSortEnd: (sortEndData: SortEndData) => void;
}
const DraggableContainer = ({
  children,
  className,
  onSortEnd,
  flow = "vertical",
  ...props
}: DraggableContainerProps) => {
  const containerRef = useFlipDraggableAnimation();

  const childrenArray = React.Children.toArray(
    children,
  ) as DraggableItemComponent[];

  const moveItem = useCallback(
    (id: DraggableItemData["id"], direction: "up" | "down") => {
      const indexOfSource = childrenArray.findIndex(
        (child) => child.props.id === id,
      );
      const indexOfDestination = indexOfSource + (direction === "up" ? -1 : 1);
      if (
        indexOfDestination < 0 ||
        indexOfDestination >= childrenArray.length
      ) {
        return;
      }
      onSortEnd({
        source: {
          id: id,
          index: indexOfSource,
        },
        destination: {
          id: childrenArray[indexOfDestination].props.id,
          index: indexOfDestination,
        },
      });
    },
    [childrenArray],
  );

  useEffect(() => {
    // Monitoring draggable items for drop events
    return monitorForElements({
      // Handler for drop event that are triggered when draggable item is dropped on the target
      onDrop({ source, location }) {
        const target = location.current.dropTargets[0];
        if (!target) return;

        const sourceData = source.data as DraggableItemData;
        const targetData = target.data as DraggableItemData;

        const indexOfSource = childrenArray.findIndex(
          (child) => child.props.id === sourceData.id,
        );
        const indexOfTarget = childrenArray.findIndex(
          (child) => child.props.id === targetData.id,
        );
        // To determine if we should drop before or after the target
        const closestEdgeOfTarget = extractClosestEdge(targetData);

        const indexOfDestination = getReorderDestinationIndex({
          closestEdgeOfTarget: closestEdgeOfTarget,
          startIndex: indexOfSource,
          indexOfTarget,
          axis: flow,
        });

        if (indexOfDestination === indexOfSource) {
          return;
        }
        onSortEnd({
          source: {
            id: sourceData.id,
            index: indexOfSource,
          },
          destination: {
            id: targetData.id,
            index: indexOfDestination,
          },
        });
      },
    });
  }, [flow, childrenArray]);

  const classes = aromaDraggableContainer();
  return (
    <DraggableContext.Provider value={{ moveItem, flow }}>
      <Box className={cx(classes, className)} {...props} ref={containerRef}>
        {children}
      </Box>
    </DraggableContext.Provider>
  );
};

DraggableContainer.Item = DraggableItem;

export default DraggableContainer;
